import React, { useState } from 'react';
import { Settings, Loader2, RotateCcw } from 'lucide-react';
import { cn } from '@/utils';
import { AISettingsData, AI_SETTINGS_RANGES } from '@/utils/aiSettingsStorage';

interface AISettingsV2Props {
  settings: AISettingsData;
  onSettingsChange: (settings: AISettingsData) => void;
  onResetSettings?: () => void;
  filesCount: number;
  onUpload: () => void;
  isLoading?: boolean;
  hasEnoughCredits?: boolean;
  className?: string;
  requiredCredits?: number;
  availableCredits?: number;
  onEmailNotificationChange?: (enabled: boolean) => void;
}

const AISettingsV2: React.FC<AISettingsV2Props> = ({
  settings,
  onSettingsChange,
  onResetSettings,
  filesCount,
  onUpload,
  isLoading = false,
  hasEnoughCredits = false,
  className,
  requiredCredits = 0,
  availableCredits = 0,
  onEmailNotificationChange
}) => {
  const [sendEmailWhenFinished, setSendEmailWhenFinished] = useState(false);
  
  const handleEmailCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const checked = e.target.checked;
    setSendEmailWhenFinished(checked);
    onEmailNotificationChange?.(checked);
  };
  
  const updateSetting = (key: keyof AISettingsData, value: number | string) => {
    onSettingsChange({
      ...settings,
      [key]: value
    });
  };

  // Preset configurations
  const presets = {
    stock: { title_length: 70, description_length: 200, keywords_count: 50, custom_instruction: '' },
    art: { title_length: 60, description_length: 150, keywords_count: 35, custom_instruction: '' },
    product: { title_length: 50, description_length: 100, keywords_count: 25, custom_instruction: '' },
    professional: { title_length: 60, description_length: 160, keywords_count: 30, custom_instruction: '' }
  };

  const applyPreset = (preset: keyof typeof presets) => {
    onSettingsChange(presets[preset]);
  };

  return (
    <div className={cn('bg-white rounded-2xl border border-gray-200 p-6', className)}>
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-2">
          <Settings className="w-5 h-5 text-purple-600" />
          <h2 className="text-xl font-bold text-foreground">AI Settings</h2>
        </div>
        {onResetSettings && (
           <button
             onClick={onResetSettings}
             className="p-2 text-gray-500 hover:text-purple-600 hover:bg-purple-50 rounded-lg transition-colors duration-200 group cursor-pointer"
             title="Reset to default settings"
           >
             <RotateCcw className="w-4 h-4 group-hover:rotate-180 transition-transform duration-300" />
           </button>
         )}
      </div>

      {/* Presets */}
      <div className="mb-6">
        <label className="text-xs font-medium text-gray-700 uppercase tracking-wide mb-2 block">Quick Presets</label>
        <div className="grid grid-cols-2 gap-2">
          <button
            onClick={() => applyPreset('stock')}
            className="px-3 py-2 text-xs font-medium bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 transition-colors"
          >
            📸 Stock Photo
          </button>
          <button
            onClick={() => applyPreset('art')}
            className="px-3 py-2 text-xs font-medium bg-purple-50 text-purple-700 rounded-lg hover:bg-purple-100 transition-colors"
          >
            🎨 Art & Design
          </button>
          <button
            onClick={() => applyPreset('product')}
            className="px-3 py-2 text-xs font-medium bg-green-50 text-green-700 rounded-lg hover:bg-green-100 transition-colors"
          >
            📦 Product
          </button>
          <button
            onClick={() => applyPreset('professional')}
            className="px-3 py-2 text-xs font-medium bg-gray-50 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
          >
            💼 Professional
          </button>
        </div>
      </div>

      <div className="space-y-6">
        {/* Max Title Length */}
        <div>
          <div className="flex justify-between items-center mb-2">
            <label className="text-sm font-medium text-foreground">
              Max Title Length
            </label>
            <span className="text-sm text-muted-foreground">
              {settings.title_length} chars
            </span>
          </div>
          <input
            type="range"
            min={AI_SETTINGS_RANGES.title_length.min}
            max={AI_SETTINGS_RANGES.title_length.max}
            value={settings.title_length}
            onChange={(e) => updateSetting('title_length', parseInt(e.target.value))}
            className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider"
          />
        </div>

        {/* Max Description Length */}
        <div>
          <div className="flex justify-between items-center mb-2">
            <label className="text-sm font-medium text-foreground">
              Max Description Length
            </label>
            <span className="text-sm text-muted-foreground">
              {settings.description_length} chars
            </span>
          </div>
          <input
            type="range"
            min={AI_SETTINGS_RANGES.description_length.min}
            max={AI_SETTINGS_RANGES.description_length.max}
            value={settings.description_length}
            onChange={(e) => updateSetting('description_length', parseInt(e.target.value))}
            className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider"
          />
        </div>

        {/* Max Keywords */}
        <div>
          <div className="flex justify-between items-center mb-2">
            <label className="text-sm font-medium text-foreground">
              Max Keywords
            </label>
            <span className="text-sm text-muted-foreground">
              {settings.keywords_count} keywords
            </span>
          </div>
          <input
            type="range"
            min={AI_SETTINGS_RANGES.keywords_count.min}
            max={AI_SETTINGS_RANGES.keywords_count.max}
            value={settings.keywords_count}
            onChange={(e) => updateSetting('keywords_count', parseInt(e.target.value))}
            className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider"
          />
        </div>

        {/* Custom Instruction */}
        <div>
          <div className="flex justify-between items-center mb-2">
            <label className="block text-sm font-medium text-foreground">
              Custom Instruction
            </label>
            <span className="text-sm text-muted-foreground">
              {settings.custom_instruction.length}/150
            </span>
          </div>
          <textarea
            value={settings.custom_instruction}
            onChange={(e) => {
              if (e.target.value.length <= 150) {
                updateSetting('custom_instruction', e.target.value);
              }
            }}
            placeholder="AI will try its best to understand your instructions, but it's recommended to leave empty for best results..."
            className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none transition-all duration-200 ${
              settings.custom_instruction.length >= 150
                ? 'border-red-300 bg-red-50'
                : 'border-gray-300'
            }`}
            rows={3}
            maxLength={150}
          />
          {settings.custom_instruction.length >= 150 && (
            <p className="text-xs text-red-500 mt-1">
              Maximum 150 characters reached
            </p>
          )}
        </div>
      </div>

      {/* Upload Button */}
      <div className="mt-8 pt-6 border-t border-gray-200">
        {/* Cost Preview */}
        {filesCount > 0 && (
          <div className="mb-4 p-4 bg-gradient-to-r from-blue-50 to-purple-50 border border-blue-200 rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-700">💰 Cost Breakdown</span>
              <span className="text-xs text-gray-500">{filesCount} files</span>
            </div>
            <div className="space-y-1.5">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Files × Credits:</span>
                <span className="font-semibold text-gray-900">{filesCount} × 1 = {requiredCredits}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Your Balance:</span>
                <span className={`font-semibold ${availableCredits >= requiredCredits ? 'text-green-600' : 'text-red-600'}`}>
                  {availableCredits} credits
                </span>
              </div>
              <div className="pt-1.5 border-t border-blue-200 flex justify-between text-sm">
                <span className="font-medium text-gray-700">After Upload:</span>
                <span className={`font-bold ${availableCredits >= requiredCredits ? 'text-blue-600' : 'text-red-600'}`}>
                  {Math.max(0, availableCredits - requiredCredits)} credits
                </span>
              </div>
            </div>
            
            {/* Insufficient Credits Warning */}
            {availableCredits < requiredCredits && (
              <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-xs text-red-700 font-medium mb-2">
                  ⚠️ Insufficient Credits
                </p>
                <p className="text-xs text-red-600 mb-2">
                  You need {requiredCredits - availableCredits} more credits to complete this upload.
                </p>
                <a
                  href="/pricing"
                  className="inline-block w-full text-center px-3 py-2 bg-red-600 text-white text-xs font-semibold rounded-lg hover:bg-red-700 transition-colors"
                >
                  Buy Credits
                </a>
              </div>
            )}
          </div>
        )}

        <button
          onClick={onUpload}
          disabled={filesCount === 0 || isLoading || !hasEnoughCredits}
          className={cn(
            'w-full py-4 px-6 rounded-xl font-semibold text-lg transition-all duration-300 shadow-lg',
            'flex items-center justify-center space-x-2',
            filesCount === 0 || isLoading || !hasEnoughCredits
              ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
              : 'bg-gradient-to-r from-blue-500 to-purple-600 text-white hover:from-blue-600 hover:to-purple-700 hover:shadow-xl transform hover:scale-[1.02]'
          )}
        >
          {isLoading ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" />
              <span>Validating Credits...</span>
            </>
          ) : (
            <>
              <span>Generate Metadata</span>
            </>
          )}
        </button>

        {/* Terms of Service & Email Notification */}
        <div className="space-y-3 mt-3">
          {filesCount === 0 && (
            <p className="text-xs text-gray-600 text-center">
              By uploading you agree to our{' '}
              <a 
                href="/terms-of-service" 
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:text-blue-700 underline"
              >
                terms of service
              </a>
            </p>
          )}

          {/* Email Notification Checkbox */}
          <label className="flex items-center justify-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={sendEmailWhenFinished}
              onChange={handleEmailCheckboxChange}
              className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
            />
            <span className="text-xs text-gray-700">
              Send email when finished
            </span>
          </label>
        </div>

        {!hasEnoughCredits && filesCount > 0 && !isLoading && (
          <p className="text-xs text-red-600 font-medium text-center mt-2">
            Insufficient credits. Purchase to continue
          </p>
        )}
      </div>
    </div>
  );
};

export default AISettingsV2;
